<div class="np-kopf">
  <div class="np-kopf__zeile">
    <h1 class="np-kopf__titel">{{ 'projektNeu.titel' | translate }}</h1>
    <span
      class="np-kopf__info"
      data-testid="np-info"
      tabindex="0"
      role="img"
      [attr.aria-label]="'projektNeu.infoLabel' | translate: { text: 'projektNeu.info' | translate }"
      [matTooltip]="'projektNeu.info' | translate"
    >
      i
    </span>
  </div>
  <p class="np-kopf__untertitel">{{ 'projektNeu.untertitel' | translate }}</p>
</div>

<ol class="np-schritte" role="list" data-testid="np-schrittleiste">
  @for (s of schritte; track s.nr) {
    @let aktiv = schritt() === s.nr;
    @let haken = erledigt(s.nr);
    <li class="np-schritte__eintrag" role="listitem">
      <button
        type="button"
        class="np-schritt"
        [class.np-schritt--aktiv]="aktiv"
        data-testid="np-schritt"
        [attr.aria-current]="aktiv ? 'step' : null"
        [disabled]="!erreichbar(s.nr)"
        (click)="schrittWaehlen(s.nr)"
      >
        <span class="np-schritt__nr" [class.np-schritt__nr--erledigt]="haken">
          @if (haken) {
            <mat-icon data-testid="np-schritt-haken" aria-hidden="true">check</mat-icon>
            <span class="cdk-visually-hidden">{{ 'projektNeu.schritt.erledigt' | translate }}</span>
          } @else {
            {{ s.nr }}
          }
        </span>
        <span class="np-schritt__text">{{ s.schluessel | translate }}</span>
        @if (s.optional) {
          &ngsp;
          <span class="np-schritt__optional">{{ 'projektNeu.schritt.optional' | translate }}</span>
        }
      </button>
    </li>
  }
</ol>

<div class="np-karte" [formGroup]="form">
  @switch (schritt()) {
    @case (1) {
      <div class="np-raster np-raster--name">
        <div>
          <label class="np-label" for="np-name">
            {{ 'projektNeu.name.label' | translate }}
            <span class="np-stern" aria-hidden="true">*</span>
          </label>
          <mat-form-field appearance="fill" subscriptSizing="dynamic" class="np-feld">
            <input
              #nameFeld
              id="np-name"
              matInput
              required
              data-testid="np-name"
              formControlName="name"
              [placeholder]="'projektNeu.name.platzhalter' | translate"
              [attr.maxlength]="fieldLengths.NAME_MAX"
              (blur)="form.controls.kuerzel.markAsTouched()"
            />
            @if (nameFehler(); as fehler) {
              <mat-error>{{ fehler | translate }}</mat-error>
            }
          </mat-form-field>
        </div>
        <div>
          <label class="np-label" for="np-kuerzel">
            {{ 'projektNeu.kuerzel.label' | translate }}
            <span class="np-stern" aria-hidden="true">*</span>
          </label>
          <mat-form-field appearance="fill" subscriptSizing="dynamic" class="np-feld np-feld--kuerzel">
            <input
              id="np-kuerzel"
              matInput
              required
              data-testid="np-kuerzel"
              formControlName="kuerzel"
              [attr.maxlength]="kuerzelMax"
              (input)="kuerzelEingabe($event)"
            />
            @if (kuerzelFehler(); as fehler) {
              <mat-error>{{ fehler | translate }}</mat-error>
            }
            <mat-hint>
              @if (vorschlagAnbieten()) {
                <button
                  type="button"
                  class="np-textknopf"
                  data-testid="np-kuerzel-vorschlag"
                  (click)="vorschlagUebernehmen()"
                >
                  {{ 'projektNeu.kuerzel.vorschlagVor' | translate }}
                  <code class="np-code">{{ vorschlag() }}</code>
                  {{ 'projektNeu.kuerzel.vorschlagNach' | translate }}
                </button>
              } @else if (werte().name.trim()) {
                {{ 'projektNeu.kuerzel.ausDemNamen' | translate }}
              } @else {
                {{ 'projektNeu.kuerzel.wirdVorgeschlagen' | translate }}
              }
            </mat-hint>
          </mat-form-field>
        </div>
      </div>

      <div class="np-raster np-raster--haelften">
        <div>
          @switch (leitungZustand()) {
            @case ('laedt') {
              <span class="np-label">{{ 'projektNeu.leitung.label' | translate }}</span>
              <div class="np-platzhalter" role="status" data-testid="np-leitung-laedt">
                {{ 'projektNeu.leitung.laedt' | translate }}
              </div>
            }
            @case ('profilFehler') {
              <span class="np-label">{{ 'projektNeu.leitung.label' | translate }}</span>
              <div class="np-platzhalter np-platzhalter--fehler" role="alert">
                <span>{{ 'projektNeu.leitung.ladefehler' | translate }}</span>
                <button type="button" class="np-textknopf" data-testid="np-leitung-erneut" (click)="erneutLaden()">
                  {{ 'projektNeu.erneut' | translate }}
                </button>
              </div>
            }
            @default {
              <app-person-select
                class="np-person"
                formControlName="leadUserId"
                [label]="'projektNeu.leitung.label' | translate"
                [options]="leitungsOptionen()"
                [allowNone]="false"
                [required]="true"
              />
              <div class="np-hinweis" data-testid="np-leitung-hinweis">{{ leitungHinweis() }}</div>
            }
          }
        </div>
        <div>
          <span class="np-label" id="np-vorlage-label">{{ 'projektNeu.vorlage.label' | translate }}</span>
          <mat-form-field appearance="fill" subscriptSizing="dynamic" class="np-feld np-feld--auswahl">
            <mat-select data-testid="np-vorlage" formControlName="vorlage" aria-labelledby="np-vorlage-label">
              <mat-option [value]="leer">{{ 'projektNeu.vorlage.leer' | translate }}</mat-option>
              @for (p of vorlagen(); track p.id) {
                <mat-option [value]="p.id">{{ 'projektNeu.vorlage.kopie' | translate: { name: p.name } }}</mat-option>
              }
            </mat-select>
            <mat-hint data-testid="np-vorlage-hinweis">
              {{
                (werte().vorlage === leer ? 'projektNeu.vorlage.hinweisLeer' : 'projektNeu.vorlage.hinweisKopie')
                  | translate
              }}
            </mat-hint>
          </mat-form-field>
        </div>
      </div>

      <div class="np-abschnitt">
        <label class="np-label" for="np-beschreibung">{{ 'projektNeu.beschreibung.label' | translate }}</label>
        <mat-form-field appearance="fill" subscriptSizing="dynamic" class="np-feld np-feld--text">
          <textarea
            id="np-beschreibung"
            matInput
            rows="3"
            data-testid="np-beschreibung"
            formControlName="beschreibung"
            [placeholder]="'projektNeu.beschreibung.platzhalter' | translate"
            [attr.maxlength]="fieldLengths.FREETEXT_MAX"
          ></textarea>
          <mat-hint align="end" aria-live="polite">
            {{ werte().beschreibung.length }} / {{ fieldLengths.FREETEXT_MAX }}
          </mat-hint>
        </mat-form-field>
      </div>
    }
    @case (2) {
      @if (vorlageLaedt()) {
        <div class="np-platzhalter" role="status" data-testid="np-vorlage-laedt">
          {{ 'projektNeu.vorlage.laedt' | translate }}
        </div>
      } @else if (schritt2(); as daten) {
        <app-umgebungen-teststufen-schritt
          data-testid="np-schritt-2"
          [daten]="daten"
          (datenChange)="schritt2.set($event)"
        />
      }
    }
    @case (3) {
      <app-team-schritt
        data-testid="np-schritt-3"
        [personen]="aktivePersonen()"
        [ichId]="ichId()"
        [ichName]="ichName()"
        [leadId]="werte().leadUserId"
        [(team)]="team"
      />
    }
  }

  <div class="np-fuss">
    @if (schritt() !== 1) {
      <button
        type="button"
        class="np-knopf np-knopf--zweit"
        data-testid="np-zurueck"
        [disabled]="speichert()"
        (click)="zurueck()"
      >
        {{ 'projektNeu.zurueck' | translate }}
      </button>
    } @else {
      <button type="button" class="np-knopf np-knopf--zweit" data-testid="np-abbrechen" (click)="abbrechen.emit()">
        {{ 'common.cancel' | translate }}
      </button>
    }
    <span class="np-fuss__luecke"></span>
    @if (schritt() === 2) {
      <button
        type="button"
        class="np-textknopf np-textknopf--gross"
        data-testid="np-ueberspringen"
        [disabled]="!schritt2Gueltig()"
        (click)="ueberspringen()"
      >
        {{ 'projektNeu.ueberspringen' | translate }}
      </button>
      <button
        type="button"
        class="np-knopf np-knopf--haupt"
        data-testid="np-weiter"
        [disabled]="!schritt2Gueltig()"
        (click)="weiter()"
      >
        {{ 'projektNeu.weiter' | translate }}
      </button>
    } @else if (schritt() === 3) {
      <button
        type="button"
        class="np-knopf np-knopf--haupt"
        data-testid="np-projekt-anlegen"
        [disabled]="speichert()"
        [attr.aria-busy]="speichert()"
        (click)="anlegen()"
      >
        {{ (speichert() ? 'projektNeu.anlegen.speichert' : 'projektNeu.anlegen.knopf') | translate }}
      </button>
    } @else {
      <button
        type="button"
        class="np-knopf np-knopf--start"
        data-testid="np-weiter"
        [disabled]="!schritt1Gueltig()"
        [title]="schritt1Gueltig() ? '' : ('projektNeu.weiterGesperrt' | translate)"
        (click)="weiter()"
      >
        {{ 'projektNeu.weiter' | translate }}
      </button>
    }
  </div>
</div>
